import { Table } from 'legacy/lut/library/list'
import { FC, useCallback, useMemo, useState } from 'react'

import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Menu } from 'core-components/library/menu'
import { useStaticCrud } from 'core-components/types/helpers'

import { useGetEspOverviewQuery } from '../../api/espApi'
import { ESP_TRANSLATION_KEYS } from '../../configs/translations'
import {
  getEspDashboardStats,
  IEspAttentionRow,
  IEspSponsorRow,
} from '../../lib/getEspDashboardStats'
import { getEspSponsorKey } from '../../lib/mapEspResponse'

import { EspStatCard } from './components/espStatCard/EspStatCard'
import { EspTypeBars } from './components/espTypeBars/EspTypeBars'
import { getAttentionColumns } from './helpers/getAttentionColumns'
import { getSponsorColumns } from './helpers/getSponsorColumns'

import styles from './EspDashboardWidget.module.scss'

const KEYS = ESP_TRANSLATION_KEYS.dashboard

const ALL_SPONSORS = 'all'

/**
 * The executive dashboard page widget (wireframe screen 6): headline coverage cards, the
 * activity-type breakdown, the per-sponsor roll-up and the partners needing attention.
 * The sponsor filter scopes everything to one sponsor's book — the wireframe's
 * "My partners (sponsor)" view; with no filter it is the manager roll-up.
 *
 * Self-contained (fetches its own data, no required props), registered as the
 * `jbp-esp-dashboard` viewModel.
 *
 * Note: the wireframe's follow-up metrics are out of the pilot's scope — the pilot has
 * no follow-ups — so those slots carry the closest available signals instead: activities
 * logged, partners without activity, and the last engagement per sponsor.
 */
export const EspDashboardWidget: FC = () => {
  const { data: overview, isLoading } = useGetEspOverviewQuery()
  const { activities, sponsorships, partnerNames } = overview ?? {}

  const [sponsorFilter, setSponsorFilter] = useState<string>(ALL_SPONSORS)

  // The sponsors are free text, so the filter offers the current sponsors of the data.
  const sponsorOptions = useMemo(() => {
    const byKey = new Map<string, string>()

    ;(sponsorships ?? []).forEach(({ currentSponsor }) => {
      if (currentSponsor) {
        byKey.set(getEspSponsorKey(currentSponsor), currentSponsor.name)
      }
    })

    return [...byKey]
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => a.value.localeCompare(b.value))
  }, [sponsorships])

  const sponsorMenuCrud = useStaticCrud({
    data: [{ key: ALL_SPONSORS, value: 'All sponsors' }, ...sponsorOptions],
    pageSize: sponsorOptions.length + 1,
  })

  const handleSponsorChoose = useCallback((sponsorKey: string) => setSponsorFilter(sponsorKey), [])

  const stats = useMemo(() => {
    const allSponsorships = sponsorships ?? []
    const scopedSponsorships =
      sponsorFilter === ALL_SPONSORS
        ? allSponsorships
        : allSponsorships.filter(
            ({ currentSponsor }) =>
              currentSponsor !== null && getEspSponsorKey(currentSponsor) === sponsorFilter
          )

    const scopedPartnerIds = new Set(scopedSponsorships.map((sponsorship) => sponsorship.partnerId))
    const scopedActivities =
      sponsorFilter === ALL_SPONSORS
        ? (activities ?? [])
        : (activities ?? []).filter((activity) => scopedPartnerIds.has(activity.partnerId))

    return getEspDashboardStats(
      scopedActivities,
      scopedSponsorships,
      partnerNames ?? {},
      Date.now()
    )
  }, [activities, partnerNames, sponsorFilter, sponsorships])

  const sponsorColumns = useMemo(() => getSponsorColumns(), [])
  const attentionColumns = useMemo(() => getAttentionColumns(), [])

  const selectedSponsorName =
    sponsorOptions.find((option) => option.key === sponsorFilter)?.value ?? null

  const sponsorFilterReplaceParams = useMemo(
    () => [{ key: '{sponsor}', value: selectedSponsorName ?? '' }],
    [selectedSponsorName]
  )

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading} />

      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>
          <Translation text={KEYS.title} span />
        </div>
      </div>

      <div className={styles.filterBar}>
        <div className={styles.filterGroup}>
          <span className={styles.filterLabel}>
            <Translation text={KEYS.viewLabel} span />
          </span>
          <span className={styles.scopeChip}>
            {selectedSponsorName ? (
              selectedSponsorName
            ) : (
              <Translation text={KEYS.viewAllPartners} span />
            )}
          </span>
        </div>

        <Menu
          source={sponsorMenuCrud}
          caption="value"
          onChoose={handleSponsorChoose}
          content={() => (
            <Button variant="text" color="grey" icon="kbase kbase-omni-chevron-down">
              {selectedSponsorName ? (
                <Translation
                  text={KEYS.sponsorFilter}
                  replaceParams={sponsorFilterReplaceParams}
                  span
                />
              ) : (
                <Translation text={KEYS.sponsorFilterAll} span />
              )}
            </Button>
          )}
        />
      </div>

      <div className={styles.cards}>
        <EspStatCard
          titleKey={KEYS.cardPartnersWithActivity}
          value={`${stats.partnersWithActivityCount} / ${stats.sponsoredPartnersCount}`}
        />
        <EspStatCard
          titleKey={KEYS.cardCoverage}
          value={stats.coveragePercent !== null ? `${stats.coveragePercent}%` : '—'}
        />
        <EspStatCard titleKey={KEYS.cardActivitiesLogged} value={stats.totalActivities} />
        <EspStatCard
          titleKey={KEYS.cardPartnersWithoutActivity}
          value={stats.partnersWithoutActivityCount}
        />
      </div>

      <div className={styles.columns}>
        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardTitle}>
              <Translation text={KEYS.byTypeTitle} span />
            </span>
          </div>

          {stats.totalActivities ? (
            <EspTypeBars items={stats.byType} />
          ) : (
            <div className={styles.empty}>
              <Translation text={KEYS.empty} span />
            </div>
          )}
        </section>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <span className={styles.cardTitle}>
              <Translation text={KEYS.bySponsorTitle} span />
            </span>
          </div>

          {stats.bySponsor.length ? (
            <Table<IEspSponsorRow>
              items={stats.bySponsor}
              keyField="sponsorKey"
              header
              columns={sponsorColumns}
            />
          ) : (
            <div className={styles.empty}>
              <Translation text={KEYS.sponsorsEmpty} span />
            </div>
          )}
        </section>
      </div>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.cardTitle}>
            <Translation text={KEYS.attentionTitle} span />
          </span>
          <span className={styles.cardHint}>
            <Translation text={KEYS.attentionHint} span />
          </span>
        </div>

        {stats.needingAttention.length ? (
          <Table<IEspAttentionRow>
            items={stats.needingAttention}
            keyField="partnerId"
            header
            columns={attentionColumns}
          />
        ) : (
          <div className={styles.empty}>
            <Translation text={KEYS.attentionEmpty} span />
          </div>
        )}
      </section>
    </div>
  )
}

EspDashboardWidget.displayName = 'EspDashboardWidget'
